<!-- SupportView.svelte -->
<script>
  import { onMount, onDestroy, tick } from 'svelte';
  import { createEventDispatcher } from 'svelte';
  import { fly } from 'svelte/transition';
  import { Capacitor } from '@capacitor/core';
  import { App } from '@capacitor/app';
  import { Keyboard } from '@capacitor/keyboard';
  import backarrow from './assets/backarrow.png';

  const dispatch = createEventDispatcher();

  let messages = [];
  let newMessage = '';
  let isLoading = true;
  let isSending = false;
  let isRefreshing = false;
  let isLoadingOlder = false;
  let canLoadMore = false;
  let messagesContainer;
  let messageInput;
  let error = null;
  let currentTicketsCount = 0;
  let currentUser = null;
  let refreshInterval = null;
  let keyboardHeight = 0;
  let isKeyboardOpen = false;

  // --- ADDED: State for file attachments ---
  let fileInput;
  let selectedFile = null;
  let selectedFilePreview = null;
  // --- END ADDED ---

  async function getCurrentUser() {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await fetch('https://groupfruits.online/users/api/GetUserProfileView/', {
        headers: { 'Authorization': `JWT ${accessToken}` },
      });
      if (response.ok) {
        currentUser = await response.json();
        // After user is loaded, re-process messages to ensure correct sender display
        if (messages.length > 0) {
          messages = messages.map(msg => ({
            ...msg,
            isCurrentUser: msg.type !== 'status' ? isMessageFromCurrentUser(msg) : undefined
          }));
        }
      }
    } catch (err) {
      console.error('Error fetching current user:', err);
    }
  }

  async function scrollToBottom() {
    await tick();
    if (messagesContainer) {
      setTimeout(() => {
        if (messagesContainer) {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
      }, 100);
    }
  }

  function isMessageFromCurrentUser(message) {
    if (!currentUser || !message) return false;
    return message.author_phone === currentUser.phone;
  }

  async function loadMessages(loadMore = false, silent = false) {
    if (loadMore) {
      isLoadingOlder = true;
    } else if (!silent) {
      isLoading = true;
    }
    if (!silent) error = null;
    
    try {
      const accessToken = localStorage.getItem('accessToken');
      const offset = loadMore ? currentTicketsCount : 0;
      
      // Using your original pagination logic
      const response = await fetch(`https://groupfruits.online/support/api/SupportMessagesAPIView/?offset=${offset}`, {
        headers: { 'Authorization': `JWT ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch messages');

      const data = await response.json();
      
      const processedMessages = [];
      const ticketGroups = {};
      
      data.messages.forEach(message => {
        if (!ticketGroups[message.ticket_id]) {
          ticketGroups[message.ticket_id] = { messages: [], ticket_info: message.ticket_info };
        }
        ticketGroups[message.ticket_id].messages.push(message);
      });
      
      // Process messages and status indicators
      Object.keys(ticketGroups).forEach(ticketId => {
        const group = ticketGroups[ticketId];
        group.messages.forEach(message => {
          processedMessages.push({ ...message, isCurrentUser: isMessageFromCurrentUser(message) });
        });
        if (group.ticket_info.status) {
          processedMessages.push({
            id: `status-${ticketId}`, type: 'status', ticket_id: ticketId,
            status: group.ticket_info.status,
            timestamp: group.messages[group.messages.length - 1]?.timestamp || new Date().toISOString()
          });
        }
      });
      
      if (loadMore) {
        const scrollHeight = messagesContainer.scrollHeight;
        messages = [...processedMessages, ...messages];
        currentTicketsCount += data.tickets_loaded || 1; // Assuming backend sends this
        await tick();
        messagesContainer.scrollTop = messagesContainer.scrollHeight - scrollHeight;
      } else {
        messages = processedMessages;
        currentTicketsCount = data.tickets_loaded || 1;
        if (!silent) setTimeout(() => scrollToBottom(), 200);
      }
      canLoadMore = data.has_more;
    } catch (err) {
      if (!silent) error = 'Unable to load messages. Please check your connection and try again.';
      console.error('Error loading messages:', err);
    } finally {
      isLoading = false;
      isLoadingOlder = false;
    }
  }

  // --- MODIFIED: sendMessage function to handle FormData ---
  async function sendMessage() {
    if ((!newMessage.trim() && !selectedFile) || isSending) return;

    isSending = true;
    const messageContent = newMessage.trim();
    const tempId = Date.now();
    
    // Create a temporary message for instant UI feedback
    const tempMessage = {
      id: tempId,
      content: messageContent,
      timestamp: new Date().toISOString(),
      author_phone: currentUser?.phone || '',
      isCurrentUser: true,
      sending: true,
      attachment_url: selectedFilePreview, // Use local blob URL for preview
      file: selectedFile, // Store the file object temporarily to be sent
    };
    
    messages = [...messages, tempMessage];
    
    // Clear inputs immediately after adding to UI
    newMessage = '';
    clearSelectedFile();
    await scrollToBottom();
    
    try {
      const accessToken = localStorage.getItem('accessToken');
      
      const formData = new FormData();
      if (messageContent) {
        formData.append('content', messageContent);
      }
      if (tempMessage.file) {
         formData.append('attachment', tempMessage.file);
      }
      
      const response = await fetch('https://groupfruits.online/support/api/SendMessageAPIView/', {
        method: 'POST',
        headers: {
          'Authorization': `JWT ${accessToken}`,
          // NOTE: Do NOT set 'Content-Type'. The browser handles it for FormData.
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Server responded with ${response.status}`);
      }
      
      const data = await response.json();
      
      // Replace the temporary message with the final one from the server
      messages = messages.map(msg => 
        msg.id === tempId ? { 
          ...data.message, 
          sending: false,
          isCurrentUser: isMessageFromCurrentUser(data.message)
        } : msg
      );
      
    } catch (err) {
      console.error('Error sending message:', err);
      // Mark the temp message as failed instead of removing it, so user can see it didn't send
      messages = messages.map(msg => msg.id === tempId ? { ...msg, sending: false, failed: true } : msg);
      error = `Failed to send message: ${err.message}`;
    } finally {
      isSending = false;
    }
  }

  // --- ADDED: Functions for handling file selection ---
  function triggerFileSelect() {
    fileInput.click();
  }

  function handleFileSelect(event) {
    const file = event.target.files[0];
    // Basic validation for image type
    if (file && file.type.startsWith('image/')) {
      selectedFile = file;
      selectedFilePreview = URL.createObjectURL(file);
    }
    event.target.value = null; // Allows selecting the same file again
  }

  function clearSelectedFile() {
    // Revoke the object URL to free up memory
    if (selectedFilePreview) {
      URL.revokeObjectURL(selectedFilePreview);
    }
    selectedFile = null;
    selectedFilePreview = null;
  }
  
  // --- Helper and Lifecycle Functions ---
  async function handleRefresh() { 
    isRefreshing = true; 
    await loadMessages(); 
    isRefreshing = false; 
  }
  
  async function silentRefresh() { 
    await loadMessages(false, true); 
  }
  
  async function loadMoreMessages() { 
    if (!canLoadMore || isLoadingOlder) return; 
    await loadMessages(true); 
  }
  
  function handleKeyPress(event) { 
    if (event.key === 'Enter' && !event.shiftKey) { 
      event.preventDefault(); 
      sendMessage(); 
    } 
  }
  
  function handleKeyboardShow(event) { 
    isKeyboardOpen = true; 
    keyboardHeight = event.keyboardHeight; 
    setTimeout(() => scrollToBottom(), 300); 
  }

  function handleKeyboardHide() { 
    isKeyboardOpen = false; 
    keyboardHeight = 0; 
    setTimeout(() => scrollToBottom(), 300); 
  }
  
  function formatTime(timestamp) {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    } else if (diffInHours < 24 * 7) {
      return `${date.toLocaleDateString('en-US', { weekday: 'short' })} ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
    } else {
      return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
    }
  }

  function getStatusText(status) {
    const texts = { 'OPEN': 'Thank-you. We will reply you shortly', 'IN_PROGRESS': 'In-Progress: We will reply you shortly', 'RESOLVED': 'This conversation has been resolved' };
    return texts[status] || 'Status unknown';
  }

  function getStatusColor(status) {
    const colors = { 'OPEN': '#25D366', 'IN_PROGRESS': '#ff9500', 'RESOLVED': '#666' };
    return colors[status] || '#666';
  }

  function goBack() { 
    dispatch('goToProfile'); 
  }
  
  onMount(async () => {
    await getCurrentUser();
    await loadMessages();
    refreshInterval = setInterval(silentRefresh, 10000);
    if (Capacitor.isNativePlatform()) {
      App.addListener('backButton', goBack);
      Keyboard.addListener('keyboardWillShow', handleKeyboardShow);
      Keyboard.addListener('keyboardWillHide', handleKeyboardHide);
    }
  });

  onDestroy(() => {
    if (refreshInterval) clearInterval(refreshInterval);
    if (Capacitor.isNativePlatform()) {
      App.removeAllListeners();
      Keyboard.removeAllListeners();
    }
    clearSelectedFile(); // Clean up object URL on component destroy
  });
</script>

<style>
  * { -webkit-user-select: none; -moz-user-select: none; -ms-user-select: none; user-select: none; }
  input, textarea { -webkit-user-select: text; -moz-user-select: text; -ms-user-select: text; user-select: text; }
  :global(body) {
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
  }
  .support-view {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen-Sans, Ubuntu, Cantarell, 'Helvetica Neue', sans-serif;
    background-color: #f5f5f5;
    height: 100vh;
    height: 100dvh;
    display: flex;
    flex-direction: column;
    position: fixed;
    width: 100%;
    top: 0;
    left: 0;
    z-index: 1000;
    overflow: hidden;
  }
  .header {
    background-color: white;
    color: #333;
    padding: calc(10px + env(safe-area-inset-top)) 16px 10px 16px;
    z-index: 10;
    display: flex;
    align-items: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    flex-shrink: 0;
  }
  .back-button { 
    width: 24px; 
    height: 24px; 
    background: none; 
    border: none; 
    padding: 0; 
	margin: 0;
    cursor: pointer; 
    margin-right: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .back-button img { 
    width: 100%; 
    height: 100%; 
    object-fit: contain; 
  }
  .header-content { 
    flex: 1; 
    display: flex; 
    align-items: center; 
    justify-content: space-between; 
  }
  .header-title { 
    font-size: 18px; 
    font-weight: 600; 
    margin: 0; 
    color: #333;
  }
  .refresh-button { 
    background: rgba(37, 211, 102, 0.1); 
    border: 1px solid rgba(37, 211, 102, 0.2); 
    color: #25D366; 
    padding: 6px 10px; 
	margin: 0;
    border-radius: 6px; 
    font-size: 14px; 
    cursor: pointer; 
    transition: background-color 0.2s; 
  }
  .refresh-button:disabled { opacity: 0.5; cursor: not-allowed; }
  .messages-container {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 20px 16px;
    background-color: #e5ddd5;
    -webkit-overflow-scrolling: touch;
  }
  .load-more-container { display: flex; justify-content: center; margin-bottom: 20px; }
  .load-more-button { background: rgba(255, 255, 255, 0.9); border: 1px solid #ddd; color: #25D366; padding: 8px 16px; border-radius: 16px; font-size: 14px; cursor: pointer; }
  .message { display: flex; margin-bottom: 12px; animation: slideIn 0.3s ease-out; }
  @keyframes slideIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
  .message.user { justify-content: flex-end; }
  .message.agent { justify-content: flex-start; }
  .message-bubble {
    max-width: 80%;
    padding: 10px 14px;
    border-radius: 18px;
    position: relative;
    word-wrap: break-word;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
  }
  .message.user .message-bubble { background-color: #dcf8c6; border-bottom-right-radius: 4px; }
  .message.agent .message-bubble { background-color: white; border-bottom-left-radius: 4px; }
  .message.sending .message-bubble { opacity: 0.7; }
  .message-attachment { margin-bottom: 8px; border-radius: 12px; overflow: hidden; max-width: 100%; cursor: pointer; }
  .message-attachment img { display: block; max-width: 100%; max-height: 250px; object-fit: cover; }
  .message-content { font-size: 16px; line-height: 1.4; color: #333; margin-bottom: 4px; white-space: pre-wrap; }
  .message-meta { display: flex; justify-content: flex-end; align-items: center; margin-top: 4px; }
  .message-sender { font-size: 11px; color: #666; font-weight: 500; text-transform: uppercase; margin-right: auto; }
  .message.user .message-sender { display: none; }
  .message-time { font-size: 12px; color: #666; margin-left: 8px; }
  .status-indicator { display: flex; justify-content: center; margin: 20px 0; }
  .status-badge { background: rgba(255, 255, 255, 0.9); border: 1px solid #ddd; padding: 8px 16px; border-radius: 20px; font-size: 13px; font-weight: 500; display: flex; align-items: center; gap: 8px; }
  .status-dot { width: 8px; height: 8px; border-radius: 50%; }
  .input-container {
    background-color: white;
    padding: 10px 16px;
    padding-bottom: calc(10px + env(safe-area-inset-bottom));
    border-top: 1px solid #e0e0e0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex-shrink: 0;
    position: relative;
    z-index: 100;
  }
  .input-row { display: flex; align-items: flex-end; gap: 12px; }
  .attach-button { width: 44px; height: 44px; background-color: #f0f0f0; border: 1px solid #ddd; border-radius: 50%; font-size: 20px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; flex-shrink: 0; }
  .file-preview-container {
    background-color: #f9f9f9; border: 1px solid #ddd; border-radius: 12px; padding: 8px; display: flex; align-items: center; justify-content: space-between;
    animation: fadeIn 0.3s;
  }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  .file-preview-image { width: 40px; height: 40px; object-fit: cover; border-radius: 8px; margin-right: 12px; }
  .file-preview-name { flex: 1; font-size: 14px; color: #333; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .file-preview-clear { background: none; border: none; font-size: 24px; color: #888; cursor: pointer; padding: 0 8px; }
  .message-input { flex: 1; border: 1px solid #ddd; border-radius: 24px; padding: 12px 16px; font-size: 16px; resize: none; max-height: 100px; min-height: 44px; font-family: inherit; background-color: #f9f9f9; }
  .message-input:focus { outline: none; border-color: #25D366; }
  .send-button { width: 44px; height: 44px; background-color: #25D366; border: none; border-radius: 50%; color: white; font-size: 18px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; flex-shrink: 0; }
  .send-button:disabled { background-color: #ccc; cursor: not-allowed; }
  .loader { display: flex; justify-content: center; align-items: center; height: 100vh; }
  .dot-loader { display: flex; }
  .dot { width: 12px; height: 12px; background-color: #25D366; border-radius: 50%; margin: 0 5px; animation: bounce 0.5s infinite alternate; }
  .dot:nth-child(2) { animation-delay: 0.1s; }
  .dot:nth-child(3) { animation-delay: 0.2s; }
  @keyframes bounce { to { transform: translateY(-10px); } }
  .error-container { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 200px; text-align: center; padding: 20px; }
  .error-message { color: #721c24; background-color: #f8d7da; border: 1px solid #f5c6cb; border-radius: 8px; padding: 15px; margin-bottom: 20px; max-width: 300px; }
  .empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 400px; text-align: center; color: #666; padding: 20px; }
  .empty-icon { font-size: 48px; margin-bottom: 16px; opacity: 0.5; }
  .empty-title { font-size: 20px; font-weight: 600; margin-bottom: 8px; }
  .spinning { animation: spin 1s linear infinite; }
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>

<div class="support-view" in:fly="{{ y: 20, duration: 300 }}">
  <div class="header">
    <button class="back-button" on:click={goBack}>
      <img src={backarrow} alt="Back" />
    </button>
    <div class="header-content">
      <h1 class="header-title">Support</h1>
      <button class="refresh-button" on:click={handleRefresh} disabled={isRefreshing}>
        <span class={isRefreshing ? 'spinning' : ''}>↻</span>
        {isRefreshing ? ' Refreshing...' : ' Refresh'}
      </button>
    </div>
  </div>

  {#if isLoading}
    <div class="loader">
      <div class="dot-loader">
        <div class="dot"></div>
        <div class="dot"></div>
        <div class="dot"></div>
      </div>
    </div>
  {:else}
    <div class="messages-container" bind:this={messagesContainer}>
      {#if error}
        <div class="error-container">
          <div class="error-message">{error}</div>
        </div>
      {/if}

      {#if canLoadMore}
        <div class="load-more-container">
          <button class="load-more-button" on:click={loadMoreMessages} disabled={isLoadingOlder}>
            {isLoadingOlder ? 'Loading...' : 'Load older messages'}
          </button>
        </div>
      {/if}

      {#if messages.length === 0 && !error}
        <div class="empty-state">
          <div class="empty-icon">💬</div>
          <div class="empty-title">Start a Conversation</div>
          <div>Send a message to get help from our support team.</div>
        </div>
      {/if}

      {#each messages as message (message.id)}
        {#if message.type === 'status'}
          <div class="status-indicator">
            <div class="status-badge">
              <div class="status-dot" style="background-color: {getStatusColor(message.status)}"></div>
              {getStatusText(message.status)}
            </div>
          </div>
        {:else}
          <div class="message {message.isCurrentUser ? 'user' : 'agent'}">
            <div class="message-bubble">
              {#if message.attachment_url}
                <a href={message.attachment_url} target="_blank" rel="noopener noreferrer" class="message-attachment">
                  <img src={message.attachment_url} alt="Attachment" />
                </a>
              {/if}
              {#if message.content}
                <div class="message-content">{message.content}</div>
              {/if}
              <div class="message-meta">
                <span class="message-sender">{!message.isCurrentUser ? 'Agent' : ''}</span>
                <span class="message-time">
                  {formatTime(message.timestamp)}
                  {#if message.sending}
                    <span style="margin-left: 4px;">⏳</span>
                  {:else if message.failed}
                    <span style="margin-left: 4px; color: red; font-weight: bold;">!</span>
                  {/if}
                </span>
              </div>
            </div>
          </div>
        {/if}
      {/each}
    </div>

    <div class="input-container">
      {#if selectedFilePreview}
        <div class="file-preview-container" transition:fly="{{ y: -10, duration: 200 }}">
          <img src={selectedFilePreview} alt="Preview" class="file-preview-image" />
          <span class="file-preview-name">{selectedFile.name}</span>
          <button class="file-preview-clear" on:click={clearSelectedFile}>×</button>
        </div>
      {/if}
    
      <div class="input-row">
        <input type="file" bind:this={fileInput} on:change={handleFileSelect} accept="image/*" style="display: none;" />
        
        <button class="attach-button" on:click={triggerFileSelect} disabled={isSending}>
          📎
        </button>

        <textarea
          bind:this={messageInput}
          bind:value={newMessage}
          placeholder="Type a message..."
          class="message-input"
          on:keypress={handleKeyPress}
          disabled={isSending}
          rows="1"
        ></textarea>
        <button 
          class="send-button" 
          on:click={sendMessage}
          disabled={(!newMessage.trim() && !selectedFile) || isSending}
        >
          {#if isSending}
            <span class="spinning">⏳</span>
          {:else}
            ➤
          {/if}
        </button>
      </div>
    </div>
  {/if}
</div>